import VideoModal from '@components/video/video-modal'
import { SanityPage } from '@data/sanity/queries/types/page'
import { SanitySiteFragment } from '@data/sanity/queries/types/site'
import { useKlaviyoIdentity } from '@lib/analytics/klaviyo'
import { LayoutContext } from '@lib/site-context'
import CookieBar from '@modules/shared/cookie-bar'
import Footer from '@modules/shared/footer'
import Header from '@modules/shared/header'
import { ReactNode, useContext, useEffect, useState } from 'react'
import { useIntersectionObserver, useWindowSize } from 'usehooks-ts'
import HeadSeo from './head-seo'

interface LayoutProps {
  site: SanitySiteFragment
  page: SanityPage
  children: ReactNode
  footer?: ReactNode
  canonicalUrl?: string
  schemas?: string[]
  draftMode?: boolean
}

const DraftModeOverlay = () => (
  <div className="sticky bottom-0 inset-x-0 z-90 bg-white bg-opacity-95">
    <div className="flex justify-between items-center container py-1">
      <p className="m-0 text-sm">Preview mode is active.</p>
      {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
      <a href="/api/disable-draft">
        <button className="underline decoration-1 hover:decoration-2 text-current text-sm">
          Exit preview mode
        </button>
      </a>
    </div>
  </div>
)

const Layout = ({
  site,
  page,
  canonicalUrl,
  schemas,
  draftMode,
  footer,
  children,
}: LayoutProps) => {
  const { headerHeight } = useContext(LayoutContext)

  const { ref: topObserverRef, isIntersecting } = useIntersectionObserver({
    freezeOnceVisible: false,
    threshold: 1,
  })
  const windowSize = useWindowSize()

  // Set window height lock (with Safari/iOS hack)
  const [lockHeight, setLockHeight] = useState(false)
  const hasChin =
    typeof window !== 'undefined' &&
    !!navigator.userAgent.match(/(iPod|iPhone|iPad)/) &&
    !!navigator.userAgent.match(/AppleWebKit/)

  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      typeof windowSize !== 'undefined' &&
      !lockHeight
    ) {
      document.body.style.setProperty('--vh', `${windowSize.height * 0.01}px`)
      setLockHeight(hasChin)
    }
  }, [hasChin, lockHeight, windowSize])

  useKlaviyoIdentity()

  return (
    <>
      <HeadSeo
        site={site}
        page={page}
        canonicalUrl={canonicalUrl}
        schemas={schemas}
      />

      <CookieBar cookieSettings={site.cookieSettings} />

      <div>
        <Header
          menuDesktopLeft={site.headerSettings?.menuDesktopLeft}
          menuDesktopRight={site.headerSettings?.menuDesktopRight}
          menuMobilePrimary={site.headerSettings?.menuMobilePrimary}
          menuMobileSecondary={site.headerSettings?.menuMobileSecondary}
          promoSettings={site.promoSettings}
          logo={site.identitySettings?.logo}
          logoSymbol={site.identitySettings?.logoSymbol}
          logoSwap={'heroLogo' in page && !!page.heroLogo}
          isInView={isIntersecting}
        />
        <div className="min-h-screen flex flex-col justify-between">
          <main id="content">
            <span
              ref={topObserverRef}
              className="relative w-full h-0 pointer-events-none"
              style={{
                top: headerHeight,
              }}
            />

            {children}
          </main>

          <Footer
            blocks={site.footerSettings?.blocks}
            copyright={site.footerSettings?.copyright}
          >
            {footer}
          </Footer>
        </div>
        <VideoModal />
      </div>

      {!!draftMode && <DraftModeOverlay />}
    </>
  )
}

export default Layout
